import { useState, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 10 * 1024 * 1024;
const OK_EXTS = ["jpg", "jpeg", "png", "webp", "gif", "pdf", "txt", "csv", "md"];

/**
 * Encapsulates ARIA's chat upload flow — paperclip + drag-and-drop both go
 * through the same `processFile` pipeline.
 *
 * Returns the handlers + state needed by the parent component:
 *   {
 *     uploading, dragActive, fileInputRef,
 *     handleFilePicked,
 *     handlePanelDragEnter, handlePanelDragLeave, handlePanelDragOver, handlePanelDrop,
 *   }
 */
export default function useFileUpload({ sessionId, setSessionId, setMessages, speakText }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef(null);

  const processFile = async (file) => {
    if (!file || uploading) return;

    // Client-side guards (server re-validates)
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!OK_EXTS.includes(ext)) {
      setMessages(prev => [...prev, { role: "assistant", content: `Sorry, I can't read .${ext} files. Try an image (JPG/PNG/WebP/GIF) or document (PDF/TXT/CSV/MD).` }]);
      return;
    }
    if (file.size > MAX_BYTES) {
      setMessages(prev => [...prev, { role: "assistant", content: `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB — please keep uploads under 10 MB.` }]);
      return;
    }

    setUploading(true);
    // Show an immediate optimistic preview so the user sees their attachment
    const optimisticPreview = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
    // crypto.randomUUID() so concurrent rapid uploads don't collide on placeholder ids.
    const placeholderId = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? `upload-${crypto.randomUUID()}`
      : `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setMessages(prev => [...prev, {
      role: "user",
      placeholderId,
      content: file.name,
      attachment: { file_name: file.name, file_type: file.type.startsWith("image/") ? "image" : "document", file_url: optimisticPreview, ext, uploading: true }
    }]);

    try {
      const headers = {};
      const token = localStorage.getItem("auth_token");
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/chat/upload?session_id=${encodeURIComponent(sessionId || "anonymous")}`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const data = res.data;
      if (optimisticPreview) {
        try { URL.revokeObjectURL(optimisticPreview); } catch { /* noop */ }
      }
      setMessages(prev => prev.map(m => m.placeholderId === placeholderId ? {
        role: "user",
        content: file.name,
        attachment: { ...data, uploading: false }
      } : m));

      // Now ask ARIA to acknowledge the upload — feed her the AI summary as context
      const contextMsg = `[Uploaded ${data.file_type}: "${data.file_name}"]\n\nFile analysis:\n${data.ai_summary}\n\nPlease acknowledge what I shared and ask any follow-up that helps me move forward.`;
      const reply = await axios.post(`${API}/chat`, { message: contextMsg, session_id: sessionId }, { headers });
      setSessionId(reply.data.session_id);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: reply.data.response,
        actionResult: reply.data.action_result || null,
      }]);
      speakText?.(reply.data.response);
    } catch (err) {
      const detail = err.response?.data?.detail || "Upload failed. Please try a different file.";
      if (optimisticPreview) {
        try { URL.revokeObjectURL(optimisticPreview); } catch { /* noop */ }
      }
      setMessages(prev => prev.map(m => m.placeholderId === placeholderId ? {
        role: "user",
        content: file.name,
        attachment: { file_name: file.name, file_type: "error", error: detail }
      } : m));
    } finally {
      setUploading(false);
    }
  };

  const handleFilePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset so the same file can be picked twice
    if (file) processFile(file);
  };

  const handlePanelDragEnter = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setDragActive(true);
  };

  const handlePanelDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragActive(false);
  };

  const handlePanelDragOver = (e) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };

  const handlePanelDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) processFile(file);
  };

  return {
    uploading,
    dragActive,
    fileInputRef,
    handleFilePicked,
    handlePanelDragEnter,
    handlePanelDragLeave,
    handlePanelDragOver,
    handlePanelDrop,
  };
}
